import React, { useEffect, useMemo, useState } from 'react';
import UserService from "../services/user.service";
import { Button, Spinner, Form, Alert } from 'react-bootstrap';
import { FaStar, FaRegStar, FaStarHalfAlt, FaComments, FaChartBar, FaCalendarAlt } from 'react-icons/fa';

const CustomerReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [activeTab, setActiveTab] = useState('uebersicht');

  const setDefaultDates = () => {
    const now = new Date();
    const end = now.toISOString().split('T')[0];
    const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const startFormatted = start.toISOString().split('T')[0];
    setStartDate(startFormatted);
    setEndDate(end);
  };

  useEffect(() => {
    setDefaultDates();
  }, []);

  const fetchCustomerReviews = async () => {
    setLoading(true);
    try {
      const response = await UserService.getCustomerReviews({
        startDate: startDate || '',
        endDate: endDate || '',
      });
      setReviews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Die Bewertungen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerReviews();
  }, [startDate, endDate]);

  const reviewList = Array.isArray(reviews) ? reviews : [];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReviews = reviewList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(reviewList.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredReviews = currentReviews.filter((review) => {
    const name = String(review.displayName || '').toLowerCase();
    const comments = Array.isArray(review.comments) ? review.comments : [];
    return name.includes(filterTerm.toLowerCase()) ||
      comments.some((comment) => comment && comment.requestDate && String(comment.requestDate).includes(filterTerm));
  });

  const renderStars = (rating) => {
    const value = Number(rating);
    if (!Number.isFinite(value)) return <span>keine Bewertung</span>;
    const fullStars = Math.floor(value);
    const halfStars = value % 1 >= 0.5 ? 1 : 0;
    const emptyStars = Math.max(0, 5 - fullStars - halfStars);
    return (
      <span className="myn-sheet__stars" aria-label={`${value} von 5 Sternen`}>
        {Array(fullStars).fill().map((_, i) => <FaStar key={`full-${i}`} aria-hidden="true" />)}
        {Array(halfStars).fill().map((_, i) => <FaStarHalfAlt key={`half-${i}`} aria-hidden="true" />)}
        {Array(emptyStars).fill().map((_, i) => <FaRegStar key={`empty-${i}`} aria-hidden="true" />)}
      </span>
    );
  };

  const summary = useMemo(() => {
    const count = reviewList.reduce((sum, review) => sum + Number(review.reviewCount || 0), 0);
    const weighted = reviewList.reduce((sum, review) => sum + Number(review.averageRating || 0) * Number(review.reviewCount || 0), 0);
    const average = count > 0 ? (weighted / count).toFixed(1) : "0";
    return { groups: reviewList.length, count, average };
  }, [reviewList]);

  const commentText = (value) => (!value || value === "No comment provided" ? "Kein Kommentar" : value);

  const tabs = [
    { id: "uebersicht", label: "Übersicht", icon: <FaChartBar aria-hidden="true" /> },
    { id: "bewertungen", label: "Bewertungen", icon: <FaComments aria-hidden="true" /> },
    { id: "zeitraum", label: "Zeitraum", icon: <FaCalendarAlt aria-hidden="true" /> },
  ];

  return (
    <section className="myn-sheet">
      <header className="myn-sheet__head">
        <span className="myn-sheet__icon" aria-hidden="true"><FaStar /></span>
        <div>
          <p className="myn-sheet__kicker">Rückmeldungen</p>
          <h1 className="myn-sheet__title">Meine Bewertungen</h1>
          <p className="myn-sheet__lead">Kennzahlen, einzelne Bewertungen und der Zeitraum sind getrennt.</p>
        </div>
      </header>

      <div className="myn-sheet__tabs" role="tablist" aria-label="Bewertungsbereiche">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={activeTab === tab.id ? "myn-sheet__tab is-active" : "myn-sheet__tab"}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <p className="myn-sheet__muted"><Spinner animation="border" size="sm" /> Bewertungen werden geladen …</p>}

      {activeTab === "uebersicht" && (
        <div className="myn-sheet__grid" role="tabpanel">
          <article className="myn-sheet__tile"><FaComments aria-hidden="true" /><span>Bewertungen</span><strong>{summary.count}</strong></article>
          <article className="myn-sheet__tile"><FaStar aria-hidden="true" /><span>Durchschnitt</span><strong>{summary.average} / 5</strong>{renderStars(summary.average)}</article>
          <article className="myn-sheet__tile"><FaChartBar aria-hidden="true" /><span>Gruppen</span><strong>{summary.groups}</strong></article>
        </div>
      )}

      {activeTab === "bewertungen" && (
        <div className="myn-sheet__panel" role="tabpanel">
          <Form.Group controlId="reviewSearch">
            <Form.Label>Suche</Form.Label>
            <Form.Control type="search" placeholder="Suche nach Name oder Datum" value={filterTerm} onChange={(e) => setFilterTerm(e.target.value)} />
          </Form.Group>
          {filteredReviews.length === 0 && !loading && <p>Keine Bewertungen im gewählten Zeitraum.</p>}
          <div className="myn-sheet__stack">
            {filteredReviews.map((review, reviewIndex) => (
              <article className="myn-sheet__review" key={`${review.displayName || "ohne"}-${reviewIndex}`}>
                <header>
                  <h2>{review.displayName || "Ohne Name"}</h2>
                  {renderStars(review.averageRating)}
                  <p>{review.reviewCount} Bewertungen · Schnitt {review.averageRating}/5</p>
                </header>
                {(Array.isArray(review.comments) ? review.comments : []).map((comment, index) => (
                  <div className="myn-sheet__comment" key={`comment-${reviewIndex}-${index}`}>
                    <p>{commentText(comment.comment)}</p>
                    <p>{renderStars(comment.rating)} <span>{comment.requestDate}</span></p>
                  </div>
                ))}
              </article>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="myn-sheet__pager">
              <Button type="button" className="myn-btn myn-btn--ghost" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>Erste</Button>
              <Button type="button" className="myn-btn myn-btn--ghost" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Zurück</Button>
              <span>Seite {currentPage} von {totalPages}</span>
              <Button type="button" className="myn-btn myn-btn--ghost" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Weiter</Button>
              <Button type="button" className="myn-btn myn-btn--ghost" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Letzte</Button>
            </div>
          )}
        </div>
      )}

      {activeTab === "zeitraum" && (
        <div className="myn-sheet__panel" role="tabpanel">
          <Form onSubmit={(event) => { event.preventDefault(); fetchCustomerReviews(); }}>
            <div className="myn-sheet__grid">
              <Form.Group controlId="reviewStart">
                <Form.Label>Startdatum</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Form.Group>
              <Form.Group controlId="reviewEnd">
                <Form.Label>Enddatum</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Form.Group>
            </div>
            <Button className="myn-btn myn-btn--primary" type="submit">Bewertungen laden</Button>
          </Form>
        </div>
      )}
    </section>
  );
};

export default CustomerReviewsPage;
